import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { getRaffle, getTickets, subscribeToTickets } from '../services/raffleService';
import { OnDemandLoader } from '../services/onDemandLoader';
import SimpleMobileTicketGrid from '../components/SimpleMobileTicketGrid';
import SimpleTicketModal from '../components/SimpleTicketModal';
import SimpleMultiTicketModal from '../components/SimpleMultiTicketModal';
import LoadingSpinner from '../components/LoadingSpinner';
import LanguageSelector from '../components/LanguageSelector';
import { useTranslation } from '../hooks/useTranslation';
import toast from 'react-hot-toast';

const MobileRafflePage = () => {
  const { raffleId } = useParams();
  const location = useLocation();
  const { t } = useTranslation();
  const [raffle, setRaffle] = useState(null);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [filter, setFilter] = useState('all');
  const [searchNumber, setSearchNumber] = useState('');
  const [selectedTickets, setSelectedTickets] = useState([]);
  const [isMultiSelectMode, setIsMultiSelectMode] = useState(false);
  const [showMultiModal, setShowMultiModal] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [randomIndices, setRandomIndices] = useState([]);
  const searchInputRef = useRef(null);
  
  // Get showAll parameter from URL
  const searchParams = new URLSearchParams(location.search);
  const showAllParam = searchParams.get('showAll');
  const showAllTickets = showAllParam !== 'false';
  
  // Debug logging
  console.log('Mobile - URL params:', location.search);
  console.log('Mobile - showAll param value:', showAllParam);
  console.log('Mobile - showAllTickets result:', showAllTickets);

  useEffect(() => {
    loadRaffle();
  }, [raffleId]);

  // Generate stable random indices when tickets change and showAll is false
  useEffect(() => {
    if (!showAllTickets && tickets.length > 0) {
      // Generate random indices only once when tickets are loaded
      if (randomIndices.length === 0) {
        const indices = Array.from({ length: tickets.length }, (_, i) => i);
        const shuffled = indices.sort(() => Math.random() - 0.5);
        const selectedIndices = shuffled.slice(0, Math.min(20, tickets.length));
        setRandomIndices(selectedIndices);
        console.log('Mobile - Generated random indices:', selectedIndices);
      }
    }
  }, [tickets.length, showAllTickets, randomIndices.length]);

  useEffect(() => {
    if (raffleId) {
      const unsubscribe = subscribeToTickets(raffleId, (updatedTickets) => {
        setTickets(updatedTickets);
      });
      return () => unsubscribe();
    }
  }, [raffleId]);

  const loadRaffle = async () => {
    try {
      setLoading(true);
      const raffleData = await getRaffle(raffleId);
      
      if (!raffleData) {
        toast.error(t('raffle.raffleNotFound'));
        return;
      }
      
      setRaffle(raffleData);
      console.log('Raffle data:', raffleData); // Debug: ver todos los campos
      await OnDemandLoader.checkAndLoadTickets(raffleId);
      
      const ticketsData = await getTickets(raffleId);
      
      if (ticketsData.length === 0) {
        setTimeout(async () => {
          const updatedTickets = await getTickets(raffleId);
          setTickets(updatedTickets);
        }, 2000);
      } else {
        setTickets(ticketsData);
      }
    } catch (error) {
      console.error('Error loading raffle:', error);
      toast.error(t('raffle.errorLoading'));
    } finally {
      setLoading(false);
    }
  };

  const handleTicketClick = (ticket) => {
    if (ticket.sold || ticket.reserved) {
      toast.error(t('tickets.ticketOccupied'));
      return;
    }
    
    if (isMultiSelectMode) {
      const isSelected = selectedTickets.find(t => t.id === ticket.id);
      if (isSelected) {
        setSelectedTickets(selectedTickets.filter(t => t.id !== ticket.id));
      } else {
        setSelectedTickets([...selectedTickets, ticket]);
      }
    } else {
      setSelectedTicket(ticket);
      setShowModal(true);
    }
  };
  
  const handleRandomSelection = (count) => {
    const availableTickets = tickets.filter(t => !t.sold && !t.reserved);
    
    if (availableTickets.length === 0) {
      toast.error(t('tickets.noAvailableTickets'));
      return;
    }
    
    if (count > availableTickets.length) {
      toast.error(t('tickets.onlyAvailable', { count: availableTickets.length }));
      return;
    }
    
    const shuffled = [...availableTickets].sort(() => 0.5 - Math.random());
    const selected = shuffled.slice(0, count);
    
    // En modo multiselección, agregar a la selección
    if (isMultiSelectMode) {
      const newSelection = [...selectedTickets];
      selected.forEach(ticket => {
        if (!newSelection.find(t => t.id === ticket.id)) {
          newSelection.push(ticket);
        }
      });
      setSelectedTickets(newSelection);
      toast.success(t('tickets.randomTicketsAdded', { count: selected.length }));
    } else {
      // Si no está en multiselección, mostrar el primer número seleccionado
      setSelectedTicket(selected[0]);
      setShowModal(true);
    }
  };
  
  const handleReserveSelected = () => {
    if (selectedTickets.length === 0) {
      toast.error(t('tickets.noTicketsSelected'));
      return;
    }
    
    setShowMultiModal(true);
  };
  
  const handleCloseMultiModal = () => {
    setShowMultiModal(false);
    setSelectedTickets([]);
    setIsMultiSelectMode(false);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedTicket(null);
  };

  const getFilteredTickets = useMemo(() => {
    console.log('Mobile - Calculating getFilteredTickets:', {
      showAllTickets,
      searchNumber: searchNumber.trim(),
      ticketsLength: tickets.length,
      randomIndicesLength: randomIndices.length
    });
    
    // Si está buscando, usar todos los tickets
    let baseTickets = tickets;
    
    // Si no está mostrando todos y no está buscando, mostrar solo una muestra
    if (!showAllTickets && !searchNumber.trim()) {
      // Use stable random indices to show the same subset
      if (randomIndices.length > 0) {
        baseTickets = randomIndices.map(index => tickets[index]).filter(Boolean);
        console.log('Mobile - Using stable subset:', baseTickets.length, 'tickets');
      } else {
        // Fallback: return empty array until random indices are generated
        baseTickets = [];
        console.log('Mobile - Waiting for random indices...');
      }
    } else {
      console.log('Mobile - Using all tickets');
    }
    
    let filtered = baseTickets;
    
    // Aplicar filtro de estado
    switch (filter) {
      case 'available':
        filtered = filtered.filter(t => !t.sold && !t.reserved);
        break;
      case 'sold':
        filtered = filtered.filter(t => t.sold || t.reserved);
        break;
      default:
        break;
    }
    
    // Aplicar búsqueda
    if (searchNumber.trim()) {
      const searchTerms = searchNumber.split(',').map(term => term.trim()).filter(term => term !== '');
      filtered = tickets.filter(ticket => {
        return searchTerms.some(term => 
          ticket.displayNumbers?.toLowerCase().includes(term.toLowerCase()) ||
          ticket.number.toString() === term
        );
      });
    }
    
    return filtered;
  }, [tickets, filter, searchNumber, showAllTickets]);

  const stats = useMemo(() => {
    const sold = tickets.filter(t => t.sold).length;
    const reserved = tickets.filter(t => t.reserved && !t.sold).length;
    const available = tickets.length - sold - reserved;
    
    return { sold, reserved, available, total: tickets.length };
  }, [tickets]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <LoadingSpinner />
          <p className="mt-4 text-gray-600">{t('raffle.loadingRaffle')}</p>
        </div>
      </div>
    );
  }

  if (!raffle) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 p-4">
        <div className="text-center bg-white rounded-xl shadow-lg p-6 max-w-sm">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-xl font-bold text-gray-800 mb-2">
            {t('raffle.raffleNotFound')}
          </h1>
          <p className="text-gray-600 text-sm">
            {t('raffle.verifyLink')}
          </p>
        </div>
      </div>
    );
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* Header Compacto para Móvil */}
      <header className="bg-gradient-to-r from-purple-600 to-pink-600 text-white sticky top-0 z-40 shadow-lg">
        <div className="p-4">
          {/* Language Selector */}
          <div className="flex justify-end mb-2">
            <LanguageSelector />
          </div>
          
          {/* Imagen de la rifa si existe */}
          {raffle.imageUrl && (
            <div className="mb-3 rounded-lg overflow-hidden shadow-md">
              <img 
                src={raffle.imageUrl} 
                alt={raffle.title}
                className="w-full h-32 object-cover"
              />
            </div>
          )}
          
          {/* Título y precio */}
          <div className="flex justify-between items-start mb-3">
            <div className="flex-1">
              <h1 className="text-lg font-bold line-clamp-1">{raffle.title}</h1>
              <p className="text-xs opacity-80">{t('common.organizer')}:</p>
              <p className="text-sm font-semibold">{raffle.organizer}</p>
            </div>
            <div className="text-right ml-4">
              <p className="text-xs opacity-80">{t('common.value')}</p>
              <p className="text-lg font-bold">{formatCurrency(raffle.price)}</p>
            </div>
          </div>
          
          {/* Estadísticas */}
          <div className="grid grid-cols-3 gap-2 text-center items-center">
            <div className="bg-white/20 rounded-lg p-2">
              <p className="text-xl font-bold">{stats.total}</p>
              <p className="text-xs">{t('common.total')}</p>
            </div>
            <div className="bg-white rounded-lg p-2 shadow-md">
              <img 
                src="/google-play-badge.svg" 
                alt="Google Play" 
                className="h-6 mx-auto mb-1"
              />
              <p className="text-xs font-bold text-gray-700 text-center">TalonarioRifas</p>
            </div>
            <div className="bg-white/20 rounded-lg p-2">
              <p className="text-sm font-bold">{raffle.lottery || 'N/A'}</p>
              <p className="text-xs">{t('common.lottery')}</p>
            </div>
          </div>
          
          {/* Número de rifa y fecha */}
          <div className="mt-3 text-center text-white">
            <p className="text-sm">
              {t('raffle.raffleNumber')}{raffle.sorteoNumero || raffle.raffleNumber || raffle.drawNumber || t('raffle.noNumber')} • {t('raffle.drawDate')} {raffle.date || '**/**/**'}
            </p>
          </div>
        </div>
      </header>

      {/* Barra de herramientas */}
      <div className="bg-white border-b sticky top-[140px] z-30">
        <div className="p-3">
          {/* Búsqueda y botones */}
          <div className="flex gap-2 mb-2">
            <div className="flex-1 relative">
              <input
                ref={searchInputRef}
                type="text"
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                placeholder={t('tickets.searchTicket')}
                className="w-full px-3 py-2 pl-9 border rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <svg className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`px-3 py-2 rounded-lg transition-colors ${
                showFilters ? 'bg-purple-600 text-white' : 'bg-gray-100 text-gray-700'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
              </svg>
            </button>
          </div>
          
          {/* Filtros expandibles */}
          {showFilters && (
            <div className="grid grid-cols-3 gap-2 mb-2">
              <button
                onClick={() => setFilter('all')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  filter === 'all' 
                    ? 'bg-purple-600 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {t('common.all')}
              </button>
              <button
                onClick={() => setFilter('available')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  filter === 'available' 
                    ? 'bg-green-600 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {t('common.available')}
              </button>
              <button
                onClick={() => setFilter('sold')}
                className={`px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                  filter === 'sold' 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-100 text-gray-700'
                }`}
              >
                {t('common.sold')}
              </button>
            </div>
          )}
          
          {/* Botones de acción rápida */}
          <div className="flex gap-2">
            <button
              onClick={() => setIsMultiSelectMode(!isMultiSelectMode)}
              className={`flex-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isMultiSelectMode 
                  ? 'bg-purple-600 text-white' 
                  : 'bg-gray-100 text-gray-700'
              }`}
            >
              {isMultiSelectMode ? t('tickets.multipleSelectionActive') : t('tickets.multipleSelection')}
            </button>
            
            <div className="flex gap-1">
              {[1, 3, 5].map(count => (
                <button
                  key={count}
                  onClick={() => handleRandomSelection(count)}
                  className="px-3 py-2 bg-purple-100 text-purple-700 rounded-lg text-sm font-medium"
                >
                  🎲 {count}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Grid de boletos virtualizado */}
      <div className="flex-1 overflow-hidden" style={{ height: raffle.imageUrl ? 'calc(100vh - 420px)' : 'calc(100vh - 280px)' }}>
        {raffle.isPrivateMode && searchNumber.trim() === '' ? (
          // Modo privado - Mostrar mensaje
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-800 mb-2">{t('privateMode.title')}</h2>
              <p className="text-gray-600 text-sm mb-4">
                {t('privateMode.description')}
              </p>
              <button
                onClick={() => searchInputRef.current?.focus()}
                className="px-6 py-3 bg-purple-600 text-white rounded-lg font-medium"
              >
                {t('privateMode.searchButton')}
              </button>
            </div>
          </div>
        ) : getFilteredTickets.length > 0 ? (
          <>
            {!showAllTickets && !searchNumber.trim() && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mx-4 mb-3">
                <p className="text-blue-800 text-sm text-center">
                  {t('raffle.showingSample')}
                </p>
              </div>
            )}
            <SimpleMobileTicketGrid
              tickets={getFilteredTickets}
              onTicketClick={handleTicketClick}
              selectedTickets={selectedTickets}
              isMultiSelectMode={isMultiSelectMode}
            />
          </>
        ) : (
          // No hay resultados
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-10 h-10 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <p className="text-gray-600">{t('tickets.noResults')}</p>
              {searchNumber && (
                <button
                  onClick={() => setSearchNumber('')}
                  className="mt-4 text-purple-600 text-sm font-medium"
                >
                  {t('tickets.clearSearch')}
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Barra inferior para selección múltiple */}
      {isMultiSelectMode && selectedTickets.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4 z-40">
          <div className="flex items-center justify-between mb-2">
            <span className="font-medium">
              {selectedTickets.length} {selectedTickets.length === 1 ? t('common.ticketsSingular') : t('common.ticketsPlural')} {selectedTickets.length === 1 ? t('common.selectedSingular') : t('common.selectedPlural')}
            </span>
            <span className="font-bold text-purple-600">
              {t('common.total')}: {formatCurrency(selectedTickets.length * raffle.price)}
            </span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={handleReserveSelected}
              className="flex-1 px-4 py-3 bg-purple-600 text-white rounded-lg font-medium"
            >
              {t('tickets.reserveAll')}
            </button>
            <button
              onClick={() => setSelectedTickets([])}
              className="px-4 py-3 bg-gray-200 text-gray-700 rounded-lg font-medium"
            >
              {t('common.clear')}
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {showModal && selectedTicket && (
        <SimpleTicketModal
          ticket={selectedTicket}
          raffle={raffle}
          raffleId={raffleId}
          onClose={handleCloseModal}
        />
      )}
      
      {showMultiModal && selectedTickets.length > 0 && (
        <SimpleMultiTicketModal
          tickets={selectedTickets}
          raffle={raffle}
          raffleId={raffleId}
          onClose={handleCloseMultiModal}
        />
      )}
    </div>
  );
};

export default MobileRafflePage;